import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { useTerritory } from '../contexts/TerritoryContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { SEO } from '../components/SEO';
import { TerritorySelector } from '../components/TerritorySelector';
import { Button } from '../components/ui/button';
import { DEFAULT_EVENT_IMAGE, handleEventImageError } from '../utils/imageUrl';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Calendar, MapPin } from 'lucide-react';
import { formatChile } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component to update map center
const MapCenterUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || 12, { duration: 1 });
    }
  }, [center, zoom, map]);
  return null;
};

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Category colors for markers
// Default fallback color
const DEFAULT_COLOR = '#00CC66';

const createCustomIcon = (color, planKey = 'basic') => {
  const baseSize = planKey === 'premium' ? 36 : planKey === 'pro' ? 32 : 28;
  const innerDot = planKey === 'premium' ? 12 : planKey === 'pro' ? 11 : 10;
  const borderWidth = planKey === 'premium' ? 4 : planKey === 'pro' ? 3.5 : 3;
  const borderColor = planKey === 'pro' ? color : 'white';
  
  let extraStyle = '';
  let extraClass = '';
  if (planKey === 'premium') {
    extraStyle = `animation: pulse-glow 1.5s ease-in-out infinite;`;
    extraClass = 'premium-pin';
  } else if (planKey === 'pro') {
    extraStyle = `box-shadow: 0 2px 12px rgba(0,0,0,0.3), 0 0 0 2px ${color}40;`;
  }
  
  return new L.DivIcon({
    className: extraClass,
    html: `<div style="background:${color || '#00CC66'};width:${baseSize}px;height:${baseSize}px;border-radius:50%;border:${borderWidth}px solid ${borderColor};box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;${extraStyle}">
      <div style="width:${innerDot}px;height:${innerDot}px;background:white;border-radius:50%;"></div>
    </div>`,
    iconSize: [baseSize, baseSize],
    iconAnchor: [baseSize / 2, baseSize / 2],
    popupAnchor: [0, -(baseSize / 2)],
  });
};

// Commune coordinates for centering
const communeCoordinates = {
  // Región Metropolitana
  'Santiago': [-33.4489, -70.6693],
  'Providencia': [-33.4272, -70.6099],
  'Las Condes': [-33.4103, -70.5673],
  'Ñuñoa': [-33.4569, -70.5973],
  'Vitacura': [-33.3939, -70.5713],
  'La Reina': [-33.4500, -70.5500],
  'Maipú': [-33.5167, -70.7500],
  'Puente Alto': [-33.6167, -70.5833],
  // Antofagasta
  'Antofagasta': [-23.6509, -70.3975],
  'Calama': [-22.4560, -68.9293],
  'San Pedro de Atacama': [-22.9087, -68.1997],
  'Tocopilla': [-22.0928, -70.1976],
  'Mejillones': [-23.0978, -70.4506],
};

// Region coordinates for centering
const regionCoordinates = {
  'Región Metropolitana': [-33.4489, -70.6693],
  'Valparaíso': [-33.0458, -71.6197],
  'Biobío': [-36.8270, -73.0503],
  'Araucanía': [-38.7396, -72.5984],
  'Los Lagos': [-41.4693, -72.9424],
  'Antofagasta': [-23.6509, -70.3975]
};

export const MapView = () => {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { translateText } = useTranslate();
  const { selectedCommune, selectedRegion } = useTerritory();
  const { formatPrice } = useCurrency();
  const { isDark } = useTheme();

  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState([-33.4489, -70.6693]);
  const [mapZoom, setMapZoom] = useState(12);
  const [legendOpen, setLegendOpen] = useState(false);

  // Update map center when commune or region changes
  useEffect(() => {
    if (selectedCommune && communeCoordinates[selectedCommune.name]) {
      setMapCenter(communeCoordinates[selectedCommune.name]);
      setMapZoom(13);
    } else if (selectedRegion && regionCoordinates[selectedRegion.name]) {
      setMapCenter(regionCoordinates[selectedRegion.name]);
      setMapZoom(10);
    }
  }, [selectedCommune, selectedRegion]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchMapEvents();
  }, [selectedCategory, dateFilter, customDate, selectedCommune, selectedRegion]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchMapEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCommune?.id) params.append('commune_id', selectedCommune.id);
      else if (selectedRegion?.id) params.append('region_id', selectedRegion.id);
      if (selectedCategory) params.append('category_id', selectedCategory);
      const activeDate = dateFilter === 'custom' ? customDate : dateFilter;
      if (activeDate && activeDate !== 'all') params.append('date_filter', activeDate);

      const response = await axios.get(`${API_URL}/api/events/map?${params}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch map events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTerritoryChange = useCallback((commune) => {
    if (commune && communeCoordinates[commune.name]) {
      setMapCenter(communeCoordinates[commune.name]);
      setMapZoom(13);
    }
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatChile(dateStr, "d MMM", locale);
  };

  return (
    <div className={`h-[calc(100vh-56px)] md:h-[calc(100vh-64px)] flex flex-col ${isDark ? 'night-page' : 'bg-[#F8FAF9]'}`} data-testid="map-page">
      <SEO
        title="Mapa de Eventos y Panoramas"
        description="Explora el mapa interactivo con panoramas, conciertos, ferias y actividades cerca de ti. Encuentra eventos por ubicacion en Chile."
        path="/map"
      />
      {/* Header - compact on mobile */}
      <div className={`border-b px-3 md:px-8 py-2 md:py-3 shrink-0 ${isDark ? 'night-header' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto space-y-1.5 md:space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-base md:text-xl font-bold ${isDark ? 'text-white' : 'text-[#064E3B]'}`}>{t('map')}</h1>
              <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {events.length} {translateText('eventos en el mapa')}
              </p>
            </div>
          </div>
          <div className="flex gap-1.5 md:gap-2 items-end overflow-x-auto pb-1 scrollbar-hide">
            <TerritorySelector 
              onComplete={handleTerritoryChange}
              showAllCommunes={true}
            />

            <Select 
              value={selectedCategory || 'all'} 
              onValueChange={(v) => setSelectedCategory(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[130px] md:w-[155px] shrink-0 rounded-xl bg-white border-gray-200 text-xs md:text-sm h-8 md:h-9" data-testid="map-category-filter">
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{translateText('Categorias')}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: cat.color || '#00CC66' }}
                      />
                      {translateText(cat.name)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div>
              <span className="text-[10px] text-gray-400 text-center block mb-0.5">¿Cuándo?</span>
              <Select 
                value={dateFilter} 
                onValueChange={(v) => { setDateFilter(v); if (v !== 'custom') setCustomDate(''); }}
              >
                <SelectTrigger className="w-[110px] md:w-[140px] shrink-0 rounded-xl bg-white border-gray-200 text-xs md:text-sm h-8 md:h-9" data-testid="map-date-filter">
                  <SelectValue placeholder="¿Cuándo?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="tomorrow">Mañana</SelectItem>
                  <SelectItem value="upcoming">Próximamente</SelectItem>
                  <SelectItem value="custom">Elegir fecha</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateFilter === 'custom' && (
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="h-8 md:h-9 px-2 md:px-3 shrink-0 rounded-xl border border-gray-200 bg-white text-xs md:text-sm"
                data-testid="map-custom-date"
              />
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative min-h-0">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00CC66]"></div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full"
            style={{ height: '100%' }}
          >
            <MapCenterUpdater center={mapCenter} zoom={mapZoom} />
            <LayersControl position="bottomleft">
              <LayersControl.BaseLayer checked name="Mapa">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satélite">
                <TileLayer
                  attribution='&copy; Esri'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            {events.map((event) => (
              event.latitude && event.longitude && (
                <Marker
                  key={event.id}
                  position={[event.latitude, event.longitude]}
                  icon={createCustomIcon(
                    categories.find(c => c.name === event.category_name)?.color || DEFAULT_COLOR,
                    event.provider_plan || 'basic'
                  )}
                >
                  <Popup className="custom-popup" minWidth={280}>
                    <div className="p-1">
                      <img
                        src={event.main_image || DEFAULT_EVENT_IMAGE}
                        alt={event.name}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                        style={{ objectPosition: `50% ${event.image_position || '50%'}` }}
                        onError={handleEventImageError}
                      />
                      <Badge variant="outline" className="mb-2 rounded-full text-xs">
                        {translateText(event.category_name)}
                      </Badge>
                      <h3 className="font-bold text-[#064E3B] text-sm mb-1">
                        {translateText(event.name)}
                      </h3>
                      {event.short_description && (
                        <p className="text-gray-500 text-xs mb-2 line-clamp-2">
                          {translateText(event.short_description)}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {event.is_permanent ? 'Permanente' : formatDate(event.start_date)}
                        </span>
                        <span className="flex items-center gap-1 font-medium text-[#00CC66]">
                          {formatPrice(event.price, event.currency) || t('freeEvent')}
                        </span>
                      </div>
                      <Button
                        asChild
                        size="sm"
                        className="w-full btn-primary text-xs py-2"
                      >
                        <Link to={`/event/${event.id}`} data-testid={`map-popup-event-link-${event.id}`}>
                          {t('viewDetails')}
                        </Link>
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        )}

        {/* Legend - collapsible on mobile */}
        <div className="absolute top-2 right-2 md:top-4 md:right-4 z-[999]">
          {/* Mobile: toggle button */}
          <button
            className="md:hidden flex items-center gap-1.5 bg-white rounded-full shadow-lg px-3 py-1.5 text-xs font-medium text-[#064E3B]"
            onClick={() => setLegendOpen(!legendOpen)}
            data-testid="legend-toggle"
          >
            <span className="flex gap-0.5">
              {categories.slice(0, 3).map(c => (
                <span key={c.id} className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || DEFAULT_COLOR }} />
              ))}
            </span>
            {legendOpen ? 'Cerrar' : 'Leyenda'}
          </button>
          {/* Legend panel - solid opaque background to stay readable over any map area */}
          <div
            className={`${legendOpen ? 'block' : 'hidden'} md:block rounded-xl p-3 md:p-4 mt-1 md:mt-0 max-w-[200px] md:max-w-[230px]`}
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              boxShadow: '0 10px 30px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.12)',
            }}
          >
            <h4 className="font-bold text-xs md:text-sm text-[#064E3B] mb-2 pb-2 border-b border-gray-200">{t('categoriesTitle')}</h4>
            <div className="grid grid-cols-1 gap-1.5 md:gap-2">
              {categories.slice(0, 8).map((cat) => (
                <div key={cat.id} className="flex items-center gap-2 text-[11px] md:text-xs">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-black/10"
                    style={{ backgroundColor: cat.color || DEFAULT_COLOR }}
                  />
                  <span className="text-gray-900 font-semibold truncate">{translateText(cat.name)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
